import React, { useEffect, useRef } from 'react';

const AnimatedBackground: React.FC<{ isMobileMenuOpen?: boolean }> = ({ isMobileMenuOpen = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: Array<{
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      hue: number;
    }> = [];

    const particleCount = 120;

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 6 + 3,
        speedX: (Math.random() - 0.5) * 1,
        speedY: (Math.random() - 0.5) * 1,
        hue: Math.random() * 80 + 160,
      });
    }

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle, index) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        ctx.shadowBlur = 25;
        ctx.shadowColor = `hsla(${particle.hue}, 100%, 60%, 1)`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${particle.hue}, 100%, 60%, 1)`;
        ctx.fill();
        ctx.shadowBlur = 0;

        particles.forEach((otherParticle, otherIndex) => {
          if (index >= otherIndex) return;
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 180) {
            ctx.beginPath();
            ctx.strokeStyle = `hsla(${particle.hue}, 100%, 60%, ${
              0.5 * (1 - distance / 180)
            })`;
            ctx.lineWidth = 2;
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      {/* Base gradient background */}
      <div className={`fixed inset-0 -z-30 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0 md:opacity-100' : ''}`} />
      
      {/* Large animated orbs using CSS */}
      <div className={`fixed inset-0 -z-20 overflow-hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0 md:opacity-100' : ''}`}>
        <div className="absolute top-10 left-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 right-20 w-80 h-80 bg-teal-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-20 left-1/4 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }} />
      </div>

      {/* Floating code elements - hidden on mobile */}
      <div className="hidden md:block fixed inset-0 -z-25 overflow-hidden text-cyan-500/30 font-mono select-none pointer-events-none">
        {/* Code brackets and symbols */}
        <div className="absolute top-20 left-[15%] text-6xl animate-float opacity-40" style={{ animationDuration: '8s' }}>{'{ }'}</div>
        <div className="absolute top-[40%] right-[20%] text-5xl animate-float opacity-35" style={{ animationDuration: '10s', animationDelay: '1s' }}>{'\<\u00a0/>'}</div>
        <div className="absolute bottom-[30%] left-[25%] text-4xl animate-float opacity-30 text-teal-500/30" style={{ animationDuration: '12s', animationDelay: '2s' }}>{'[\u00a0]'}</div>
        <div className="absolute top-[60%] right-[15%] text-5xl animate-float opacity-40 text-emerald-500/30" style={{ animationDuration: '9s', animationDelay: '1.5s' }}>{'=>'}</div>
        
        {/* Code snippets */}
        <div className="absolute top-[25%] left-[10%] text-sm animate-float opacity-30 text-teal-400/40" style={{ animationDuration: '11s' }}>
          const code = true;
        </div>
        <div className="absolute top-[15%] right-[25%] text-sm animate-float opacity-25 text-cyan-400/40" style={{ animationDuration: '13s', animationDelay: '2s' }}>
          function() {'{}'} 
        </div>
        <div className="absolute bottom-[25%] right-[30%] text-sm animate-float opacity-35 text-emerald-400/45" style={{ animationDuration: '10s', animationDelay: '1s' }}>
          npm run dev
        </div>
        <div className="absolute top-[50%] left-[5%] text-xs animate-float opacity-25 text-blue-400/40" style={{ animationDuration: '14s', animationDelay: '3s' }}>
          git commit -m "update"
        </div>
        
        {/* Binary code */}
        <div className="absolute top-[35%] right-[10%] text-xs animate-float opacity-20 text-cyan-300/30" style={{ animationDuration: '15s' }}>
          01001000 11010001
        </div>
        <div className="absolute bottom-[40%] left-[20%] text-xs animate-float opacity-20 text-teal-300/30" style={{ animationDuration: '16s', animationDelay: '2.5s' }}>
          10110101 01101110
        </div>
        
        {/* Terminal symbols */}
        <div className="absolute top-[45%] left-[30%] text-3xl animate-float opacity-35 text-emerald-500/35" style={{ animationDuration: '9s', animationDelay: '1s' }}>$</div>
        <div className="absolute bottom-[35%] right-[25%] text-3xl animate-float opacity-30 text-cyan-500/30" style={{ animationDuration: '11s', animationDelay: '2s' }}>~</div>
        
        {/* Tech stack keywords */}
        <div className="absolute top-[70%] left-[15%] text-xs animate-float opacity-25 text-emerald-400/35 font-semibold" style={{ animationDuration: '13s', animationDelay: '1s' }}>
          REACT
        </div>
        <div className="absolute top-[55%] right-[35%] text-xs animate-float opacity-25 text-cyan-400/35 font-semibold" style={{ animationDuration: '12s', animationDelay: '2.5s' }}>
          NODE.JS
        </div>
        <div className="absolute bottom-[45%] left-[35%] text-xs animate-float opacity-25 text-teal-400/35 font-semibold" style={{ animationDuration: '14s' }}>
          TYPESCRIPT
        </div>
        
        {/* More code symbols */}
        <div className="absolute top-[80%] right-[20%] text-2xl animate-float opacity-30 text-blue-400/30" style={{ animationDuration: '10s', animationDelay: '1.5s' }}>{'()'}</div>
        <div className="absolute bottom-[55%] right-[40%] text-xl animate-float opacity-25 text-teal-500/30" style={{ animationDuration: '11s', animationDelay: '0.5s' }}>;</div>
      </div>


      {/* Animated wave overlay */}
      <div 
        className={`fixed inset-0 -z-24 opacity-30 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0 md:opacity-30' : ''}`}
        style={{
          backgroundImage: `
            radial-gradient(circle at 20% 50%, rgba(6, 182, 212, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 20%, rgba(20, 184, 166, 0.15) 0%, transparent 50%)
          `,
          animation: 'wave 15s ease-in-out infinite'
        }}
      />
      
      {/* Canvas for particle network */}
      <canvas
        ref={canvasRef}
        className={`fixed top-0 left-0 w-full h-full -z-10 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0 md:opacity-100' : ''}`}
      />
      
      {/* Animation keyframes */}
      <style>{`
        @keyframes wave {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-10px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }
      `}</style>
    </>
  );
};

export default AnimatedBackground;
